import React from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Close, DeleteOutline, EventOutlined, ListAltOutlined, StarOutline } from '@material-ui/icons'
import { CommissionSettlement, CommissionType } from '../types'
import { money, typeLabel } from '../utils'

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 12,
    overflow: 'hidden'
  },
  header: {
    padding: theme.spacing(2.5, 3),
    color: '#fff',
    background: 'linear-gradient(120deg, #f56600 0%, #ff8533 100%)',
    '@media (max-width: 760px)': { padding: theme.spacing(2) }
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  eyebrow: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
    letterSpacing: '0.08em',
    opacity: 0.84,
    textTransform: 'uppercase'
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.2
  },
  closeButton: {
    flexShrink: 0,
    color: '#fff',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
    '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 }
  },
  content: {
    padding: theme.spacing(3),
    backgroundColor: '#f7f8fa',
    '@media (max-width: 760px)': { padding: theme.spacing(2) }
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    '@media (max-width: 760px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    '@media (max-width: 420px)': { gridTemplateColumns: '1fr' }
  },
  summaryCard: {
    minWidth: 0,
    padding: theme.spacing(1.75),
    border: '1px solid #e3e6ea',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(31, 38, 46, 0.04)'
  },
  summaryIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    color: '#e86100',
    backgroundColor: '#fff1e7'
  },
  summaryLabel: {
    color: '#66707a',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  summaryValue: {
    marginTop: theme.spacing(0.35),
    color: '#20242a',
    fontWeight: 750,
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'anywhere'
  },
  totalCard: {
    borderColor: '#ffc99f',
    backgroundColor: '#fff8f3'
  },
  totalValue: {
    color: '#d95700'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.25)
  },
  sectionTitle: {
    color: '#2a2f36',
    fontWeight: 750
  },
  countChip: {
    color: '#9a4100',
    fontWeight: 700,
    backgroundColor: '#ffeadb'
  },
  tableContainer: {
    border: '1px solid #e1e4e8',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(31, 38, 46, 0.04)',
    overscrollBehavior: 'contain'
  },
  tableHead: {
    '& .MuiTableCell-head': {
      color: '#525b65',
      fontSize: '0.75rem',
      fontWeight: 750,
      letterSpacing: '0.035em',
      backgroundColor: '#f1f3f5',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap'
    }
  },
  tableRow: {
    '&:nth-of-type(even)': { backgroundColor: '#fbfcfd' },
    '&:hover': { backgroundColor: '#fff7f1' }
  },
  conceptChip: {
    height: 26,
    fontWeight: 700
  },
  numericCell: {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  amountCell: {
    color: '#20242a',
    fontWeight: 750
  },
  emptyState: {
    padding: theme.spacing(4),
    color: '#66707a',
    textAlign: 'center'
  },
  actions: {
    justifyContent: 'space-between',
    padding: theme.spacing(1.75, 3),
    borderTop: '1px solid #e5e8eb',
    backgroundColor: '#fff',
    '@media (max-width: 760px)': { padding: theme.spacing(1.5, 2) }
  },
  deleteButton: {
    color: '#b42318',
    borderColor: '#f0b4ae',
    '&:hover': {
      borderColor: '#b42318',
      backgroundColor: '#fff2f0'
    },
    '&:focus-visible': { outline: '2px solid #b42318', outlineOffset: 2 }
  },
  closeAction: {
    fontWeight: 700,
    '&:focus-visible': { outline: '2px solid #f56600', outlineOffset: 2 }
  }
}))

const conceptColors: Record<CommissionType, { color: string; backgroundColor: string }> = {
  office: { color: '#9a4100', backgroundColor: '#fff0e4' },
  success: { color: '#176b3a', backgroundColor: '#e8f6ed' },
  timely_payment: { color: '#3156a3', backgroundColor: '#edf2ff' }
}

const formatPeriod = (period?: string) => {
  if (!period) return '—'
  const [year, month] = period.split('-').map(Number)
  if (!year || !month) return period
  const formatted = new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 1))
  )
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

const formatPaidDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('es-CO', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'America/Bogota'
      }).format(new Date(value))
    : '—'

interface Props {
  settlement?: CommissionSettlement
  canDelete?: boolean
  mutating?: boolean
  onClose: () => void
  onDelete?: () => void
}

const SettlementDetail: React.FC<Props> = ({ settlement, canDelete, mutating, onClose, onDelete }) => {
  const classes = useStyles()
  const lineCount = settlement?.lineItems.length || 0

  return (
    <Dialog
      open={Boolean(settlement)}
      onClose={mutating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.paper }}
      aria-labelledby="settlement-detail-title"
    >
      <DialogTitle disableTypography className={classes.header}>
        <Box className={classes.headerRow}>
          <Box minWidth={0}>
            <Typography className={classes.eyebrow} variant="caption">
              Comisiones del ejecutivo
            </Typography>
            <Typography className={classes.title} variant="h5" id="settlement-detail-title">
              Detalle de liquidación
            </Typography>
          </Box>
          <IconButton className={classes.closeButton} aria-label="Cerrar detalle" onClick={onClose} disabled={mutating}>
            <Close aria-hidden="true" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.summaryGrid}>
          <Paper elevation={0} className={classes.summaryCard}>
            <Box className={classes.summaryIcon}>
              <EventOutlined fontSize="small" aria-hidden="true" />
            </Box>
            <Typography className={classes.summaryLabel}>Período de oficina</Typography>
            <Typography className={classes.summaryValue}>{formatPeriod(settlement?.period)}</Typography>
          </Paper>
          <Paper elevation={0} className={classes.summaryCard}>
            <Box className={classes.summaryIcon}>
              <ListAltOutlined fontSize="small" aria-hidden="true" />
            </Box>
            <Typography className={classes.summaryLabel}>Fecha de pago</Typography>
            <Typography className={classes.summaryValue}>{formatPaidDate(settlement?.paidAt)}</Typography>
          </Paper>
          <Paper elevation={0} className={classes.summaryCard}>
            <Box className={classes.summaryIcon}>
              <StarOutline fontSize="small" aria-hidden="true" />
            </Box>
            <Typography className={classes.summaryLabel}>Bono de meta</Typography>
            <Typography className={classes.summaryValue}>
              {settlement?.goalBonusIncluded ? money(settlement.goalBonus) : 'No incluido'}
            </Typography>
          </Paper>
          <Paper elevation={0} className={`${classes.summaryCard} ${classes.totalCard}`}>
            <Typography className={classes.summaryLabel}>Total liquidado</Typography>
            <Typography className={`${classes.summaryValue} ${classes.totalValue}`} variant="h6">
              {money(settlement?.totalAmount || 0)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {lineCount} {lineCount === 1 ? 'concepto' : 'conceptos'}
            </Typography>
          </Paper>
        </Box>

        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle} variant="h6">
            Conceptos incluidos
          </Typography>
          <Chip className={classes.countChip} size="small" label={`${lineCount} en total`} />
        </Box>

        <TableContainer className={classes.tableContainer} component={Paper}>
          <Table size="small" aria-label="Conceptos incluidos en la liquidación">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Concepto</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Mes del evento</TableCell>
                <TableCell>Período de oficina</TableCell>
                <TableCell align="right">Base sin IVA</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settlement?.lineItems.map((item, index) => (
                <TableRow className={classes.tableRow} key={`${item.personId}-${item.type}-${index}`}>
                  <TableCell>
                    <Chip
                      className={classes.conceptChip}
                      size="small"
                      label={typeLabel[item.type]}
                      style={conceptColors[item.type]}
                    />
                  </TableCell>
                  <TableCell>{item.eventDate}</TableCell>
                  <TableCell>{item.eventPeriod}</TableCell>
                  <TableCell>{item.officePeriod}</TableCell>
                  <TableCell className={classes.numericCell} align="right">
                    {money(item.feeWithoutVat)}
                  </TableCell>
                  <TableCell className={`${classes.numericCell} ${classes.amountCell}`} align="right">
                    {money(item.amount)}
                  </TableCell>
                </TableRow>
              ))}
              {!lineCount ? (
                <TableRow>
                  <TableCell colSpan={6} className={classes.emptyState}>
                    Esta liquidación no contiene conceptos individuales.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <Divider />
      <DialogActions className={classes.actions}>
        <Box>
          {canDelete && settlement?.status === 'paid' ? (
            <Button
              className={classes.deleteButton}
              variant="outlined"
              startIcon={<DeleteOutline aria-hidden="true" />}
              onClick={onDelete}
              disabled={mutating}
            >
              Eliminar liquidación
            </Button>
          ) : null}
        </Box>
        <Button
          className={classes.closeAction}
          color="primary"
          variant="contained"
          onClick={onClose}
          disabled={mutating}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SettlementDetail
